import AuthMedia from "./AuthMedia";
import { Calendar, Heart, Clock, Sparkles, Infinity as InfinityIcon, Music2, Image as ImageIcon, Video, CalendarClock } from "lucide-react";

const META = {
  immediate: { label: "Condiviso", icon: Sparkles },
  date: { label: "Alla data", icon: Calendar },
  event: { label: "All'evento", icon: Clock },
  death: { label: "Dopo la scomparsa", icon: Heart },
};

const keyDate = (m) => m.release_date || m.created_at;
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};
const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};

const MemoryTimeline = ({ memories, guardianToken, ownerName, coverImage }) => {
  const sorted = [...memories].sort((a, b) => new Date(keyDate(a)) - new Date(keyDate(b)));

  return (
    <div data-testid="memory-timeline">
      {coverImage && (
        <div className="rounded-3xl overflow-hidden mb-8 relative" data-testid="timeline-cover">
          <AuthMedia item={coverImage} guardianToken={guardianToken} className="w-full h-56 sm:h-72 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
          <p className="absolute bottom-5 left-6 right-6 font-serif text-3xl text-white">La Capsula del Tempo di {ownerName}</p>
        </div>
      )}
      {/* Infinity header */}
      <div className="flex flex-col items-center text-center mb-10">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FBEDE7] to-[#EAF2ED] flex items-center justify-center mb-3 shadow-sm">
          <InfinityIcon className="w-8 h-8 text-primary" strokeWidth={1.5} />
        </div>
        <p className="font-serif text-2xl">La capsula del tempo di {ownerName}</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">Un filo che non si spezza: i ricordi, in ordine, come pagine di una vita che continua.</p>
      </div>

      <div className="relative pl-8 sm:pl-0">
        {/* central line */}
        <div className="absolute top-0 bottom-0 left-4 sm:left-1/2 w-px bg-gradient-to-b from-primary/40 via-border to-[#81B29A]/40 sm:-translate-x-1/2" aria-hidden />

        <div className="space-y-12">
          {sorted.map((m, idx) => {
            const meta = META[m.release_type] || META.death;
            const Icon = meta.icon;
            const left = idx % 2 === 0;
            return (
              <div key={m.id} className={`relative sm:grid sm:grid-cols-2 sm:gap-10 items-center ${left ? "" : ""}`} data-testid={`timeline-item-${m.id}`}>
                {/* node */}
                <div className="absolute left-4 sm:left-1/2 -translate-x-1/2 z-10">
                  <div className="w-4 h-4 rounded-full bg-white border-2 border-primary shadow" />
                </div>

                {/* date badge + card, alternating sides on desktop */}
                <div className={`hidden sm:block ${left ? "order-1 text-right pr-2" : "order-2 col-start-2 text-left pl-2"}`}>
                  <div className={`inline-flex flex-col ${left ? "items-end" : "items-start"}`}>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{fmtDate(keyDate(m))}</span>
                    {m.release_type === "event" && m.event_description && (
                      <span className="text-xs text-[#C99700]">{m.event_description}</span>
                    )}
                  </div>
                </div>

                <div className={`ml-10 sm:ml-0 ${left ? "sm:order-2 sm:col-start-2 sm:pl-2" : "sm:order-1 sm:col-start-1 sm:pr-2"}`}>
                  <article className="bg-white rounded-2xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow text-left">
                    <span className="inline-flex items-center gap-1.5 text-xs text-primary bg-[#FBEDE7] px-3 py-1 rounded-full mb-2">
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                    <p className="sm:hidden text-xs text-muted-foreground mb-2">{fmtDate(keyDate(m))}</p>
                    <h3 className="font-serif text-xl mb-2">{m.title}</h3>
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5" data-testid="timeline-created-at">
                      <CalendarClock className="w-3.5 h-3.5" /> Ricordo creato il {fmtDate(m.created_at)}
                    </p>
                    {m.recipients?.length > 0 && (
                      <p className="text-xs text-muted-foreground mb-2">Per: {m.recipients.join(", ")}</p>
                    )}
                    {m.content && <p className="whitespace-pre-wrap leading-relaxed text-[#3d3a38] text-sm">{m.content}</p>}
                    {m.song && (
                      <div className="flex items-center gap-2 bg-[#F0EAF4] rounded-lg p-2 mt-3" data-testid="timeline-song">
                        <Music2 className="w-4 h-4 text-[#8A6D9C] shrink-0" />
                        <div className="flex-1"><AuthMedia item={m.song} guardianToken={guardianToken} /></div>
                      </div>
                    )}
                    {(m.media || []).some((x) => x.kind === "image") && (
                      <div className="mt-3" data-testid="timeline-photos">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-[#81B29A]" /> Foto</p>
                        <div className="grid grid-cols-2 gap-2">
                          {(m.media || []).filter((x) => x.kind === "image").map((item) => (
                            <div key={item.storage_path} className="rounded-lg overflow-hidden border border-border">
                              <AuthMedia item={item} guardianToken={guardianToken} className="w-full h-28 object-cover" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(m.media || []).some((x) => x.kind === "video") && (
                      <div className="mt-3" data-testid="timeline-videos">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-primary" /> Videomessaggi</p>
                        <div className="grid grid-cols-2 gap-2">
                          {(m.media || []).filter((x) => x.kind === "video").map((item) => (
                            <div key={item.storage_path} className="rounded-lg overflow-hidden border border-border relative">
                              <AuthMedia item={item} guardianToken={guardianToken} className="w-full h-28 object-cover" />
                              {(item.uploaded_at || m.created_at) && (
                                <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-2 py-0.5 flex items-center gap-1">
                                  <CalendarClock className="w-3 h-3" /> {fmtDay(item.uploaded_at || m.created_at)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(m.media || []).some((x) => x.kind !== "image" && x.kind !== "video") && (
                      <div className="grid grid-cols-1 gap-2 mt-3">
                        {(m.media || []).filter((x) => x.kind !== "image" && x.kind !== "video").map((item) => (
                          <div key={item.storage_path} className="rounded-lg overflow-hidden border border-border p-2 bg-[#F4F1EA]">
                            <AuthMedia item={item} guardianToken={guardianToken} />
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                </div>
              </div>
            );
          })}
        </div>

        {/* infinity end cap */}
        <div className="relative mt-10 flex justify-center sm:justify-center">
          <div className="absolute left-4 sm:left-1/2 -translate-x-1/2">
            <InfinityIcon className="w-6 h-6 text-[#81B29A]" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryTimeline;
